// frontend/src/components/JobRoleModal.jsx
import { useState, useEffect } from 'react';
import api from '../services/api';

export default function JobRoleModal({ show, onClose, onSave, editRole = null }) {
    const [permissions, setPermissions] = useState([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const permissionGroups = [
        {
            title: "🔍 Visualização",
            items: [
                { id: "view_dashboard", label: "Dashboard de Leitos" },
                { id: "view_services", label: "Painel de Serviços" },
                { id: "view_metrics", label: "Métricas e Relatórios" },
                { id: "access_nir", label: "Acessar Módulo NIR (Reservas)" }, // [NEW]
            ]
        },
        {
            title: "⚡ Operacional",
            items: [
                { id: "perform_checkin", label: "Realizar Check-in" },
                { id: "perform_checkout", label: "Realizar Check-out" },
                { id: "manage_bed_status", label: "Alterar Status do Leito" },
                { id: "manage_service_status", label: "Gerenciar Serviços (Limpeza/Manut.)" },
            ]
        },
        {
            title: "⚙️ Configurações e Gestão",
            items: [
                { id: "manage_beds", label: "Criar/Excluir Leitos" },
                { id: "manage_sectors", label: "Criar/Excluir Setores" },
                { id: "manage_users", label: "Gerenciar Usuários" },
                { id: "manage_settings", label: "Configurações do Sistema" },
            ]
        }
    ];

    useEffect(() => {
        if (show) {
            if (editRole) {
                setName(editRole.name);
                setDescription(editRole.description || '');
                setPermissions(editRole.permissions || []);
            } else {
                resetForm();
            }
        }
    }, [show, editRole]);

    const resetForm = () => {
        setName('');
        setDescription('');
        setPermissions([]);
        setError('');
    };

    const togglePermission = (permId) => {
        setPermissions(prev =>
            prev.includes(permId)
                ? prev.filter(p => p !== permId)
                : [...prev, permId]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const payload = { name, description, permissions };
            if (editRole) {
                await api.patch(`/job-roles/${editRole.id}`, payload);
            } else {
                await api.post('/job-roles', payload);
            }

            if (onSave) onSave();
            onClose();
            resetForm();
        } catch (error) {
            console.error('Erro ao salvar função:', error);
            setError(error.response?.data?.error || 'Erro ao salvar função');
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-800 flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50 rounded-t-2xl">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <i className="bi bi-shield-lock"></i>
                        {editRole ? 'Editar Função e Permissões' : 'Nova Função'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    >
                        <i className="bi bi-x-lg text-lg"></i>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                    <div className="p-6 overflow-y-auto space-y-6">
                        {error && (
                            <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">
                                {error}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome da Função *</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    disabled={loading}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-black/20 focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                    placeholder="Ex: Recepcionista"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrição</label>
                                <input
                                    type="text"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    disabled={loading}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-black/20 focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                    placeholder="Opcional"
                                />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h4 className="font-semibold text-gray-900 dark:text-white pb-2 border-b border-gray-100 dark:border-gray-800">
                                Permissões de Acesso
                            </h4>

                            <div className="grid grid-cols-1 gap-6">
                                {permissionGroups.map((group, idx) => (
                                    <div key={idx} className="bg-gray-50 dark:bg-gray-800/30 p-4 rounded-xl border border-gray-100 dark:border-gray-800">
                                        <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                            {group.title}
                                        </h5>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {group.items.map(perm => (
                                                <label key={perm.id} className="flex items-center gap-3 p-2 hover:bg-white dark:hover:bg-gray-800 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={permissions.includes(perm.id)}
                                                        onChange={() => togglePermission(perm.id)}
                                                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm text-gray-600 dark:text-gray-400 select-none">
                                                        {perm.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 rounded-b-2xl flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-lg"></i>
                                    Salvar Alterações
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
